import type { ScenarioWorkspaceItemPanel } from '../model/types'

import { useCallback, useMemo, useReducer } from 'react'

import { uuid } from '@/shared/tools/utils'

type Panel = Pick<
	ScenarioWorkspaceItemPanel,
	'scenarioId' | 'pricingCampaignId' | 'part'
>

export const createPanel = (panel?: Panel) => ({
	id: uuid(),
	scenarioId: null,
	pricingCampaignId: null,
	part: 'settings' as const,
	...panel,
})
const initialState = {
	panels: [createPanel()],
}

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case 'clear_panels':
			return {
				panels: [createPanel()],
			}
		case 'add_panel':
			return {
				...state,
				panels: [...state.panels, createPanel(action.panel)],
			}
		case 'remove_panel': {
			const newPanels = state.panels.filter((panel) => panel.id !== action.id)

			return {
				...state,
				panels: newPanels?.length === 0 ? [createPanel()] : newPanels,
			}
		}
		case 'update_panel':
			return {
				...state,
				panels: state.panels.map((panel) =>
					panel.id === action.id ? { ...panel, ...action.data } : panel
				),
			}
		default:
			return state
	}
}

type State = {
	panels: ScenarioWorkspaceItemPanel[]
}
type Action =
	| {
			type: 'add_panel'
			panel?: Panel
	  }
	| {
			type: 'remove_panel'
			id: string
	  }
	| {
			type: 'update_panel'
			id: string
			data: Partial<ScenarioWorkspaceItemPanel>
	  }
	| {
			type: 'clear_panels'
	  }

export const usePanelsStore = () => {
	const [state, dispatch] = useReducer(reducer, initialState)
	const updatePanel = useCallback(
		(id: string, data: Partial<ScenarioWorkspaceItemPanel>) => {
			dispatch({ type: 'update_panel', id, data })
		},
		[dispatch]
	)
	const addPanel = useCallback(
		(panel?: Panel) => {
			dispatch({ type: 'add_panel', panel })
		},
		[dispatch]
	)
	const removePanel = useCallback(
		(id: string) => {
			dispatch({ type: 'remove_panel', id })
		},
		[dispatch]
	)
	const clearPanels = useCallback(() => {
		dispatch({ type: 'clear_panels' })
	}, [dispatch])

	return useMemo(
		() => ({
			panels: state.panels,
			updatePanel,
			addPanel,
			removePanel,
			clearPanels,
		}),
		[state.panels, updatePanel, addPanel, removePanel, clearPanels]
	)
}
